import * as z from "zod";
import {
  type SupplyIntelCategory,
  supplyIntelCategoryExclusion,
} from "./category";
import type { SupplyIntelConfig, SupplyIntelEstimateParams } from "./config";
import { liquidationValuePerUnit, resaleGainPerUnit } from "./estimate";
import {
  listPriceUsd,
  recommendNextStep,
  SupplyIntelGradeStatus,
  SupplyIntelNextStep,
} from "./next-step";
import { SupplyIntelPool } from "./pool";

/** Age from the unit's creation date, banded at the refulfillment timer and `longAgedDays`. */
export enum SupplyIntelAgeBand {
  Fresh = "fresh",
  Aged = "aged",
  LongAged = "long_aged",
}

export const SUPPLY_INTEL_RESALE_STEPS: ReadonlySet<SupplyIntelNextStep> =
  new Set([SupplyIntelNextStep.Resale, SupplyIntelNextStep.ResalePreOwned]);
export const SUPPLY_INTEL_LOT_OR_LIQUIDATE_STEPS: ReadonlySet<SupplyIntelNextStep> =
  new Set([SupplyIntelNextStep.OffPriceLot, SupplyIntelNextStep.Liquidate]);
export const SUPPLY_INTEL_NEEDS_REVIEW_STEPS: ReadonlySet<SupplyIntelNextStep> =
  new Set([
    SupplyIntelNextStep.BrandRejected,
    SupplyIntelNextStep.NeedsGrade,
    SupplyIntelNextStep.NeedsPrice,
    SupplyIntelNextStep.ReviewHold,
  ]);

/** Returned units of one brand's product in one warehouse that share a grade, pool, routing, timer state, age band and price. */
export interface SupplyIntelBrandUnitGroup {
  warehouseId: string | null;
  warehouseName: string | null;
  /** Null for an undeclared unit with no product. */
  productId: string | null;
  sku: string | null;
  title: string | null;
  /** The store's product the variant belongs to; null when Redo has none. */
  productFamilyId: string | null;
  productFamilyTitle: string | null;
  /** The product's category; null when it has none or it's "Uncategorized". */
  category: SupplyIntelCategory | null;
  grade: string | null;
  gradeStatus: SupplyIntelGradeStatus;
  /** Null while the units are still in process, outside any bin. */
  pool: SupplyIntelPool | null;
  /** From `supplyIntelRoutedTo`. */
  routedTo: string | null;
  pastRefulfillmentTimer: boolean;
  ageBand: SupplyIntelAgeBand;
  oldestAgeDays: number;
  currency: string | null;
  /** One unit's list price in `currency`. */
  listPrice: number | null;
  units: number;
}

const SupplyIntelBrandUnitRowSchema_ = z.object({
  id: z.string(),
  warehouseId: z.string().nullable(),
  warehouseName: z.string().nullable(),
  productId: z.string().nullable(),
  sku: z.string().nullable(),
  title: z.string().nullable(),
  // Defaulted so an older server's response still parses during a deploy.
  productFamilyId: z.string().nullable().default(null),
  // Defaulted so an older server's response still parses during a deploy.
  productFamilyTitle: z.string().nullable().default(null),
  /** The category's full path; null without one. */
  // Defaulted so an older server's response still parses during a deploy.
  category: z.string().nullable().default(null),
  grade: z.string().nullable(),
  gradeStatus: z.enum(SupplyIntelGradeStatus),
  pool: z.enum(SupplyIntelPool).nullable(),
  pastRefulfillmentTimer: z.boolean(),
  ageBand: z.enum(SupplyIntelAgeBand),
  oldestAgeDays: z.number(),
  units: z.number(),
  currency: z.string().nullable(),
  listPrice: z.number().nullable(),
  /** Null, like `reason`, while the units are in process. */
  nextStep: z.enum(SupplyIntelNextStep).nullable(),
  reason: z.string().nullable(),
});
export const SupplyIntelBrandUnitRowSchema: z.ZodType<SupplyIntelBrandUnitRow> =
  SupplyIntelBrandUnitRowSchema_;
export interface SupplyIntelBrandUnitRow extends z.infer<
  typeof SupplyIntelBrandUnitRowSchema_
> {}

/**
 * Everything the next step depends on, so units across age bands make one
 * lot. Grades not sold as new go to resale whatever their bin or timer, so
 * those units on a shelf make one lot too. A lot never spans warehouses.
 */
function lotKey(group: SupplyIntelBrandUnitGroup): string {
  const splitsByBinAndTimer =
    group.gradeStatus !== SupplyIntelGradeStatus.NotSoldAsNew;
  return JSON.stringify([
    group.warehouseId,
    group.productId,
    group.grade,
    splitsByBinAndTimer || group.pool === null ? group.pool : "on_shelf",
    group.routedTo,
    splitsByBinAndTimer ? group.pastRefulfillmentTimer : null,
    group.currency,
    group.listPrice,
  ]);
}

/** The ID of the unit row a group of units belongs to. */
export function supplyIntelBrandUnitRowId(
  group: SupplyIntelBrandUnitGroup,
): string {
  return JSON.stringify([
    group.warehouseId,
    group.productId,
    group.grade,
    group.pool,
    group.routedTo,
    group.pastRefulfillmentTimer,
    group.currency,
    group.listPrice,
    group.ageBand,
  ]);
}

/**
 * One row per warehouse, product, grade, pool, routing, timer state, age band
 * and price, with its next step. The off-price lot rule counts the units of every age
 * band that share the rest, since the bands only split how long units waited.
 */
export function buildSupplyIntelBrandUnitRows(
  groups: readonly SupplyIntelBrandUnitGroup[],
  config: SupplyIntelConfig,
): SupplyIntelBrandUnitRow[] {
  const merged = new Map<string, SupplyIntelBrandUnitGroup>();
  for (const group of groups) {
    const key = supplyIntelBrandUnitRowId(group);
    const existing = merged.get(key);
    if (existing) {
      existing.units += group.units;
      existing.oldestAgeDays = Math.max(
        existing.oldestAgeDays,
        group.oldestAgeDays,
      );
    } else {
      merged.set(key, { ...group });
    }
  }

  const lotUnits = new Map<string, number>();
  for (const group of merged.values()) {
    const key = lotKey(group);
    lotUnits.set(key, (lotUnits.get(key) ?? 0) + group.units);
  }

  return [...merged].map(([id, group]) => {
    const lot = lotUnits.get(lotKey(group)) ?? group.units;
    const recommendation =
      group.pool === null
        ? null
        : recommendNextStep(
            {
              currency: group.currency,
              excludedCategory: supplyIntelCategoryExclusion(
                group.category,
                group.gradeStatus,
                config,
              ),
              gradeStatus: group.gradeStatus,
              listPrice: group.listPrice,
              pastRefulfillmentTimer: group.pastRefulfillmentTimer,
              pool: group.pool,
              routedTo: group.routedTo,
              units: lot,
            },
            config,
          );
    const reason =
      recommendation?.step === SupplyIntelNextStep.OffPriceLot &&
      lot !== group.units
        ? `${recommendation.reason}, counting this product's other rows at this grade in this warehouse`
        : (recommendation?.reason ?? null);
    return {
      id,
      warehouseId: group.warehouseId,
      warehouseName: group.warehouseName,
      productId: group.productId,
      sku: group.sku,
      title: group.title,
      productFamilyId: group.productFamilyId,
      productFamilyTitle: group.productFamilyTitle,
      category: group.category?.name ?? null,
      grade: group.grade,
      gradeStatus: group.gradeStatus,
      pool: group.pool,
      pastRefulfillmentTimer: group.pastRefulfillmentTimer,
      ageBand: group.ageBand,
      oldestAgeDays: group.oldestAgeDays,
      units: group.units,
      currency: group.currency,
      listPrice: group.listPrice,
      nextStep: recommendation?.step ?? null,
      reason,
    };
  });
}

/** The row's units at list in approximate USD; null without a price in a currency the config has a rate for. */
export function supplyIntelValueUsd(
  row: Pick<SupplyIntelBrandUnitRow, "currency" | "listPrice" | "units">,
  config: SupplyIntelConfig,
): number | null {
  const priceUsd = listPriceUsd(row, config);
  return priceUsd === null ? null : row.units * priceUsd;
}

/** Where resale upside is estimated; next steps always use eBay, the live channel. */
export enum SupplyIntelChannel {
  Ebay = "ebay",
  /** A scenario, not a live channel. */
  RedoMarketplace = "redo_marketplace",
}

export function supplyIntelChannelEstimate(
  channel: SupplyIntelChannel,
  config: SupplyIntelConfig,
): SupplyIntelEstimateParams {
  switch (channel) {
    case SupplyIntelChannel.Ebay:
      return config.estimate;
    case SupplyIntelChannel.RedoMarketplace:
      return { ...config.estimate, ...config.redoMarketplace };
  }
}

/** Sell-through scenarios for the resale buffer; next steps always use the config's expected sell-through. */
export enum SupplyIntelSellThroughLever {
  Expected = "expected",
  Thirty = "thirty",
  Fifty = "fifty",
}

export function supplyIntelLeverSellThrough(
  lever: SupplyIntelSellThroughLever,
  config: SupplyIntelConfig,
): number {
  switch (lever) {
    case SupplyIntelSellThroughLever.Expected:
      return config.expectedSellThrough;
    case SupplyIntelSellThroughLever.Thirty:
      return 0.3;
    case SupplyIntelSellThroughLever.Fifty:
      return 0.5;
  }
}

/** Units that leave the brand's store: resale, an off-price lot or liquidation. */
export function isSupplyIntelActionable(
  step: SupplyIntelNextStep | null,
): boolean {
  return (
    step !== null &&
    (SUPPLY_INTEL_RESALE_STEPS.has(step) ||
      SUPPLY_INTEL_LOT_OR_LIQUIDATE_STEPS.has(step))
  );
}

/**
 * What liquidating the row's units today recovers, in approximate USD, never
 * below zero (handling outweighs the share of a very low price); null
 * without a price in a known currency.
 */
export function supplyIntelLiquidationFloorUsd(
  row: Pick<SupplyIntelBrandUnitRow, "currency" | "listPrice" | "units">,
  config: SupplyIntelConfig,
): number | null {
  const priceUsd = listPriceUsd(row, config);
  return priceUsd === null
    ? null
    : row.units *
        Math.max(0, liquidationValuePerUnit(priceUsd, config.estimate));
}

/**
 * The extra the row's units recoup by reselling on `channel` instead of
 * liquidating today, at `sellThrough`, in approximate USD; zero where listing
 * doesn't beat liquidating. Grades not sold as new resell as Pre-owned.
 */
export function supplyIntelResaleBufferUsd(
  row: Pick<
    SupplyIntelBrandUnitRow,
    "currency" | "gradeStatus" | "listPrice" | "units"
  >,
  sellThrough: number,
  config: SupplyIntelConfig,
  channel: SupplyIntelChannel,
): number {
  const priceUsd = listPriceUsd(row, config);
  if (priceUsd === null) {
    return 0;
  }
  const gainPerUnit = resaleGainPerUnit({
    listPriceUsd: priceUsd,
    params: supplyIntelChannelEstimate(channel, config),
    preOwned: row.gradeStatus === SupplyIntelGradeStatus.NotSoldAsNew,
    sellThrough,
    sellThroughHorizonDays: config.sellThroughHorizonDays,
    windowDays: config.resaleWindowDays,
  });
  return row.units * Math.max(0, gainPerUnit);
}
